import React from 'react';
import cn from 'classnames';
import { includes } from 'lodash';
import { BetaLabel } from 'components/BetaProgram';
import { PracticeTypes } from 'constants/practiceTypes';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import { useFeatureFlags } from 'components/shared/hooks';
import QuestionHint from './QuestionHint';
import QuestionOptions from './QuestionOptions';

const ElementHeader = ({
  options,
  handleUpdate,
  applyChanges,
  hasOptions,
  content,
  id,
  isTabbedElement,
}) => {
  const titles = {
    chat: t('lessons.editor.elements.chat_title'),
    email_composition: t('lessons.editor.elements.email_composition_title'),
    email_reply: t('lessons.editor.elements.email_reply_title'),
    free_response: t('lessons.editor.elements.free_response_title'),
    knowledge_check: t('lessons.editor.elements.knowledge_check_title'),
    sorting: t('lessons.editor.elements.sorting_title'),
    multiple_choice: t('lessons.editor.elements.multiple_choice_title'),
    multiple_select: t('lessons.editor.elements.multiple_select_title'),
    question_bank: t('lessons.editor.elements.question_bank_title'),
    screen_recording: t('lessons.editor.elements.screen_recording_title'),
    survey: t('lessons.editor.elements.survey_title'),
    ticket_handling: t('lessons.editor.elements.ticket_handling_title'),
    video_response: 'Video Response',
    webcam_recording: t('lessons.editor.elements.webcam_recording_title'),
    audio_recording: t('lessons.editor.elements.audio_recording_title'),
    written_response: t('lessons.editor.elements.written_response_title'),
    zendesk_ticket_handling: t(
      'lessons.editor.elements.zendesk_ticket_handling_title',
    ),
    matching: t('lessons.editor.elements.matching_title'),
    upload_response: t('lessons.editor.elements.upload_response_title'),
  };

  const { question_type: questionType } = content;

  const title = () => {
    if (content.zendesk) {
      return titles.zendesk_ticket_handling;
    }
    return titles[questionType];
  };

  const isPracticeQuestion = PracticeTypes.includes(questionType);
  const canUsePracticeFeature = useFeatureFlags('practice');

  const hasFeedbackCriteria =
    canUsePracticeFeature && isPracticeQuestion
      ? content.criteria?.length > 0
      : false;

  const isKnowledgeCheckElement = includes(
    'knowledge_check sorting',
    questionType,
  );
  const isInteractiveElement = includes('matching', questionType);
  const isBetaLabel = isInteractiveElement;

  const isGraderNotesEnabled = content.question_associated_comments?.find(
    e => e.commentType === 'note_to_grader',
  )?.active;

  return (
    <div
      className={cn('EditorElement-header p-32', {
        'border-bottom-0 pl-32 pr-32 pb-16 pt-32': isTabbedElement,
        'border-bottom-0': questionType === 'question_bank',
      })}
    >
      <div className="d-f ai-c jc-sb">
        <Text className="mb-4 d-f ai-c" variant={TEXT_VARIANTS.SUBHEADER}>
          {title()} {isBetaLabel && <BetaLabel className="ml-8" whiteOnBlack />}
        </Text>
        {hasOptions && (
          <QuestionOptions
            id={id}
            options={options}
            onChange={handleUpdate}
            applyChanges={applyChanges}
            questionMetadata={{
              hasFeedbackCriteria,
              isKnowledgeCheckElement,
              isInteractiveElement,
              isGraderNotesEnabled,
            }}
            isInteractiveElement={isInteractiveElement}
          />
        )}
      </div>
      <QuestionHint
        questionType={questionType}
        isValid={content.valid}
        isZendesk={content.zendesk}
      />
    </div>
  );
};

export default ElementHeader;
